<template>
  <div class="bgColor card-wrapper cust_mrg">
    <div v-if="keysLoading" class="card-spinner-container">
      <VProgressRing connotation="cta" />
    </div>

    <div v-else>
      <div class="heading-wrapper heading-border">
        <span class="customer-info-title paddingBottom">Manage API Keys</span>
        <VButton connotation="cta" appearance="filled" label="Add Key" :disabled="appDetails.status === 'revoked'" @click="$emit('addApiKey')" > </VButton>
      </div>

      <AppTable
        class="custom-table"
        :columns="clientColumns"
        :rows="clientData"
      >
        <template #client_id="{ value }">
          <div class="url-row">
            <span class="url-text" :title="value">{{ value }}</span>
            <VButton
              class="copy-btn"
              appearance="ghost"
              connotation="cta"
              size="condensed"
              @click="$emit('copy', value)"
            >
              <template #icon>
                <VIcon name="copy-2-line" />
              </template>
            </VButton>
          </div>
        </template>

        <template #client_secret="{ row, value }">
          <div v-if="loadingKeyId === row.id" class="spinner-inline">
            <VProgressRing connotation="cta" class="vvd-spinner-sm" />
          </div>
          <div v-else class="url-row">
            <template v-if="value">
              <span class="url-text" :title="value">{{ value }}</span>
              <VButton
                class="copy-btn"
                appearance="ghost"
                connotation="cta"
                size="condensed"
                @click="$emit('copy', value)"
              >
                <template #icon>
                  <VIcon name="copy-2-line" />
                </template>
              </VButton>
            </template>
            <template v-else>
              <span class="masked-text">**************</span>
              <VButton
                class="copy-btn"
                appearance="ghost"
                connotation="cta"
                size="condensed"
                title="Show Client Secret"
                @click="$emit('fetchClientSecretById', row.id, 'client_secret')"
              >
                <template #icon>
                  <VIcon name="eye-line" />
                </template>
              </VButton>
            </template>
          </div>
        </template>

        <template #status="{ value }">
          <span class="status-chip" :style="getChipStyle(value)">
            {{ value.charAt(0).toUpperCase() + value.slice(1) }}
          </span>
        </template>

        <template #created_at="{ value }">
          {{ dateFormatter(value) }}
        </template>

        <template #updated_at="{ value }">
          {{ dateFormatter(value) }}
        </template>

        <template #access_token_expiry_at="slotProps">
          <span v-if="slotProps?.value">
            {{ dateFormatter(slotProps.value, true) }}
          </span>
          <span v-else>N/A</span>
        </template>
        <template #access_token_expiry_seconds="slotProps">
          <span v-if="slotProps?.value">
            {{ formatSeconds(slotProps.value) }}
          </span>
          <span v-else>N/A</span>
        </template>

        <template #actions="{ row }">
          <ActionPopOver
            side="top"
            alignment="right"
            :style="{ '--width': '215px' }"
          >
            <div class="action-list">
              <div
                v-if="row.access_token_expiry_at === null"
                :class="['action-item', { disabled: appDetails.status === 'revoked' }]"
                @click="appDetails.status !== 'revoked' && $emit('openTokenModal', row)"
              >
                <VIcon name="plus-line"></VIcon>
                Create Access Token
              </div>

              <div
                v-else
                :class="['action-item', { disabled: appDetails.status === 'revoked' }]"
                @click="appDetails.status !== 'revoked' && $emit('openTokenModal', row)"
              >
                <VIcon name="refresh-line"></VIcon>
                Refresh Access Token
              </div>

              <div
                :class="['action-item', { disabled: appDetails.status === 'revoked' }]"
                @click="appDetails.status !== 'revoked' && $emit('updateKeyStatus', row.id, row.status)"
              >
                <VIcon name="backspace-line" />
                {{
                  row.status?.toLowerCase() === "active"
                    ? "Revoke Key"
                    : "Activate Key"
                }}
              </div>

              <div
                :class="['action-item', { disabled: appDetails.status === 'revoked' }]"
                @click="appDetails.status !== 'revoked' && $emit('deleteKey', row.id)"
              >
                <VIcon name="delete-line" connotation="alert" />
                Delete Key
              </div>
            </div>
          </ActionPopOver>
        </template>
      </AppTable>
    </div>
  </div>
</template>

<script>
import AppTable from "@/components/CustomTable.vue";
import ActionPopOver from "@/components/ActionPopOver.vue";
export default {
  name: "ApiKeysSection",
  components: { AppTable, ActionPopOver },
  emits: ['addApiKey', 'copy', 'fetchClientSecretById', 'openTokenModal', 'updateKeyStatus', 'deleteKey'],
  props: {
    keysLoading: Boolean,
    clientColumns: Array,
    clientData: Array,
    appDetails: Object,
    dateFormatter: Function,
    getChipStyle: Function,
    loadingKeyId: {
      type: [String, Number],
      default: null,
    },
  },
  methods: {
    formatExpiryDuration(value) {
      if (!value || typeof value !== "string") return "";

      const unit = value.slice(-1).toLowerCase();
      const num = value.slice(0, -1);

      if (isNaN(num)) return "";

      switch (unit) {
        case "d":
          return `${num} day${num > 1 ? "s" : ""}`;
        case "h":
          return `${num} hour${num > 1 ? "s" : ""}`;
        case "m":
          return `${num} minute${num > 1 ? "s" : ""}`;
        default:
          return "";
      }
    },
    formatSeconds(seconds) {
      if (typeof seconds !== "number" || seconds <= 0) return "";

      const days = Math.floor(seconds / (24 * 60 * 60));
      const hours = Math.floor((seconds % (24 * 60 * 60)) / (60 * 60));
      const minutes = Math.floor((seconds % (60 * 60)) / 60);

      const parts = [];
      if (days > 0) parts.push(`${days} day${days > 1 ? "s" : ""}`);
      if (hours > 0) parts.push(`${hours} hour${hours > 1 ? "s" : ""}`);
      if (minutes > 0) parts.push(`${minutes} min${minutes > 1 ? "s" : ""}`);

      return parts.join(" ");
    },
  },
};
</script>

<style lang="scss" scoped>
.card-wrapper {
  border-radius: 8px;
}
.card-spinner-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 180px;
}
.spinner-inline {
  display: flex;
  align-items: center;
}
.heading-border {
  border-bottom: 1px solid var(--background-primary);
  padding: 20px;
}
.heading-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.status-chip {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
}
.url-row {
  display: flex;
  align-items: center;
  gap: 6px;
}
.url-text {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.masked-text {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.copy-btn {
  flex-shrink: 0;
}
.custom-table {
  padding: 0px 20px;
  overflow: visible;
  :deep(.table-row) {
    overflow: visible;
  }
  :deep(.cell) {
    overflow: visible;
  }
  :deep(.table-scroll-container) {
    overflow: visible;
  }
  :deep(.app-table) {
    overflow: visible;
  }
}
.action-list {
  display: flex;
  flex-direction: column;
}
.action-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 15px;
  cursor: pointer;
  font-size: 14px;
  &:hover {
    background: var(--purple-opac);
    color: var(--purple);
    font-weight: 700;
  }
  &.disabled {
    opacity: 0.4;
    pointer-events: none;
  }
}
</style>
